import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import { clampNumber, numberFromConfig } from "../shared/feature-value.ts";
import {
  applyNavigationEffectValue,
  resetNavigationEffectValue,
} from "../shared/page-navigation-effects.ts";

const attribute = "data-migrosone-a11y-focus-emphasis";
const focusVariables = [
  "--migrosone-a11y-focus-outline-width",
  "--migrosone-a11y-focus-outline-offset",
  "--migrosone-a11y-focus-outline-color",
  "--migrosone-a11y-focus-shadow-spread",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function readFocusConfig(config: unknown) {
  return {
    outlineOffset: clampNumber(numberFromConfig(config, "outlineOffset", 3), 0, 8),
    outlineWidth: clampNumber(numberFromConfig(config, "outlineWidth", 4), 1, 8),
  };
}

function resetFocusVariables(document: Document) {
  for (const variable of focusVariables) {
    resetNavigationEffectValue({ attribute, document, variable });
  }
}

function applyFocusStyle(document: Document, config: unknown) {
  const focusConfig = readFocusConfig(config);
  applyNavigationEffectValue({
    attribute,
    document,
    value: `${focusConfig.outlineWidth}px`,
    variable: "--migrosone-a11y-focus-outline-width",
  });
  applyNavigationEffectValue({
    attribute,
    document,
    value: `${focusConfig.outlineOffset}px`,
    variable: "--migrosone-a11y-focus-outline-offset",
  });
  applyNavigationEffectValue({
    attribute,
    document,
    value: "#f7c948",
    variable: "--migrosone-a11y-focus-outline-color",
  });
  applyNavigationEffectValue({
    attribute,
    document,
    value: "2px",
    variable: "--migrosone-a11y-focus-shadow-spread",
  });
}

const definition = getFeaturePanelDefinition("focus-emphasis");

if (!definition) {
  throw new Error("Missing focus-emphasis feature definition");
}

export const focusEmphasisFeatureModule: RuntimeFeatureModule = {
  key: "focus-emphasis",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Odak vurgusu";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetFocusVariables(document);
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyFocusStyle(document, config);
  },
  reset({ config, document }) {
    resetFocusVariables(document);
    if (readBooleanDefault(config)) {
      applyFocusStyle(document, config);
    }
  },
};
